/**
 * ACCOUNT.ACTIONS
 * Actions related to idtree accounts
 */

import {
  Account,
  AccountAdsOptions,
  AccountColors,
  AccountOptions,
  AccountTemplateRule,
  ACCOUNT_ACTIVATE,
  ACCOUNT_ADD,
  ACCOUNT_EDIT,
  ACCOUNT_GET,
  ACCOUNT_GET_COUNT,
  ACCOUNT_GET_COUNT_SEARCH,
  ACCOUNT_GET_RPS_SURVEYS,
  ACCOUNT_INIT,
  ACCOUNT_ORDER,
  ACCOUNT_REMOVE,
  ACCOUNT_STATUS,
  createEmptyAccountRisksSettings,
  normalizeRiskAttributes,
  RiskSurveyStatus,
  RpsSurvey
} from "./account.types"
import { fetchBackend } from "@/core/apis/backend"
import { CONNEXION_MODE } from "./_session.types"
import { sessionEdit } from "./_session.actions"

export const accountActivate = (account: Account) => ({
  type: ACCOUNT_ACTIVATE,
  payload: {
    account
  }
})

export const accountAdd = (supervisorId, firstname, lastname) => ({
  type: ACCOUNT_ADD,
  payload: {
    supervisorId,
    firstname,
    lastname
  }
})

export const accountEdit = (key: string, value: any) => ({
  type: ACCOUNT_EDIT,
  payload: {
    key,
    value
  }
})

export const accountRisksSettingsEdit = ({ key, value }: { key: string; value: any }) => (dispatch, getState) => {
  const account = getState().account.active
  const settings = getState()._session.accountRisksSettings
  dispatch(sessionEdit("accountRisksSettings", { ...settings, [key]: value }))
  return dispatch(accountEdit("accountRisksSettings", { ...(account.accountRisksSettings ?? {}), [key]: value }))
}

export const accountRisksSettingsSet = (settings: any) => (dispatch) => {
  if (!settings) {
    dispatch(accountEdit("accountRisksSettings", null))
    dispatch(sessionEdit("accountRisksSettings", createEmptyAccountRisksSettings()))
    return
  }
  //Normalise une seule fois, a l'entree : le reste de l'app ne voit que des attributs { id, name }
  const riskAttributes = normalizeRiskAttributes(settings.riskAttributes)
  dispatch(accountEdit("accountRisksSettings", {
    allowMultipleEstablishments: settings.allowMultipleEstablishments ?? false,
    legalIdentifier: settings.legalIdentifier,
    severityScale: settings.severityScale,
    frequencyScale: settings.frequencyScale,
    preventionScale: settings.preventionScale,
    residualRiskScale: settings.residualRiskScale,
    residualRiskLevels: settings.residualRiskLevels,
    isIdtreeScoreHidden: settings.isIdtreeScoreHidden,
    riskAttributes,
    riskSurveys: Array.isArray(settings.riskSurveys) ? settings.riskSurveys : []
  }))
  dispatch(sessionEdit("accountRisksSettings", { ...settings, riskAttributes }))
}

export const accountGetRpsSurveys = (rpsSurveys: RpsSurvey[]) => ({
  type: ACCOUNT_GET_RPS_SURVEYS,
  payload: {
    rpsSurveys
  }
})

//Enquetes RPS eligibles du compte, avec la decision prise sur chacune
export const accountFetchRpsSurveys = () => async (dispatch) => {
  const response: any = await fetchBackend({ url: "/account/rps-surveys" })
  const rpsSurveys: RpsSurvey[] = Array.isArray(response) ? response : []

  dispatch(accountGetRpsSurveys(rpsSurveys))
  return rpsSurveys
}

//Activation indépendante du mapping, conservé lors de la désactivation.
export const accountSetRpsSurveyStatus =
  (surveyId: string, status: RiskSurveyStatus) => async (dispatch) => {
    const response = await fetchBackend({ url: `/account/rps-surveys/${surveyId}/status`, method: "PUT", data: { status } })
    if (response?.error) throw new Error(String(response.error))
    dispatch(accountRisksSettingsEdit({ key: "riskSurveys", value: response.riskSurveys }))
    return response
  }

//Toutes les enquetes reviennent a "non defini". Le mapping RPS n'est pas concerne.
export const accountResetRpsSurveyStatuses = () => async (dispatch) => {
  const response = await fetchBackend({ url: "/account/rps-surveys/status", method: "DELETE" })
  if (response?.error) throw new Error(String(response.error))
  dispatch(accountRisksSettingsEdit({ key: "riskSurveys", value: response.riskSurveys }))
  return response
}

export const accountGet = (accounts: Account[]) => ({
  type: ACCOUNT_GET,
  payload: {
    accounts
  }
})

export const accountGetCount = (count: number) => ({
  type: ACCOUNT_GET_COUNT,
  payload: {
    count
  }
})

export const accountGetCountSearch = (countSearch: number) => ({
  type: ACCOUNT_GET_COUNT_SEARCH,
  payload: {
    countSearch
  }
})

export const accountInit = () => ({
  type: ACCOUNT_INIT
})

export const accountOrder = (ascOrDesc: "asc" | "desc", order: string) => ({
  type: ACCOUNT_ORDER,
  payload: {
    ascOrDesc,
    order
  }
})

export const accountRemove = (id: string) => ({
  type: ACCOUNT_REMOVE,
  payload: {
    id
  }
})

export const accountStatus = (status: string) => ({
  type: ACCOUNT_STATUS,
  payload: {
    status
  }
})

//API

export const accountConnect = (accountId, email, accountDemoSurvey) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/connect",
    data: {
      accountId,
      email,
      accountDemoSurvey
    }
  }
})

export const accountConnect_AsSupervisor = (accountId, email, accountDemoSurvey) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/supervisor/account/connect",
    data: {
      accountId,
      email,
      accountDemoSurvey
    }
  }
})

//Import du jeu de demonstration risques (plusieurs etablissements) sur un compte
export const accountImportDemoRisks_AsSupervisor = (accountId) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/supervisor/establishment/import-demo",
    data: {
      accountId
    }
  }
})

export const accountConvertToSuperaccount = (accountId) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/supervisor/account/convert-to-superaccount",
    data: {
      accountId
    }
  }
})

export const accountConfirm = (userId, code) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/public/account/confirm",
    data: {
      userId,
      code
    }
  }
})

export const accountDestroy = (id: string) => ({
  type: "API",
  payload: {
    method: "DELETE",
    url: "/account/" + id
  }
})

export const accountDestroy_AsSupervisor = (id: string) => ({
  type: "API",
  payload: {
    method: "DELETE",
    url: "/supervisor/account/" + id
  }
})

//for multiple, get all sub-accounts
export const accountFetch_AsSuperadmin = () => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/superadmin/accounts"
  }
})

//same function as above, returns only acount's id, name, options and colors (used for superadmin lateral bar)
export const accountFetchLite_AsSuperadmin = () => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/superadmin/accounts/lite"
  }
})

export const accountFetch = (search: string) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/accounts",
    data: {
      search
    }
  }
})

export const accountFetch_AsSupervisor = (
  search: string,
  offset: number,
  limit: number,
  showTrials: boolean,
  salesId: string | null = null
) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/supervisor/accounts",
    data: {
      search,
      offset,
      limit,
      showTrials,
      salesId
    }
  }
})

export const accountFetchCount_AsSuperadmin = (search: string) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/superadmin/account/count",
    data: {
      search
    }
  }
})

export const accountFetchOne_AsSupervisor = (id: string) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/supervisor/account",
    data: {
      id
    }
  }
})

export const accountFetchCount_AsSupervisor = (
  search: string,
  showTrials: boolean,
  salesId: string | null = null
) => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/supervisor/account/count",
    data: {
      search,
      showTrials,
      salesId
    }
  }
})

export const accountFetchParents_AsSupervisor = () => ({
  type: "API",
  payload: {
    method: "GET",
    url: "/supervisor/accounts/parents"
  }
})

export const accountInsertPublic = (
  accountAdsOptions: AccountAdsOptions,
  accountColors: AccountColors,
  connexionMode: CONNEXION_MODE,
  email: string,
  id: string,
  language: string,
  name: string,
  firstname: string,
  lastname: string,
  phone: string,
  isWithImage: boolean,
  googlePhotoURL: string | null,
  turnstileToken: string
) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/public/account/insert",
    data: {
      accountAdsOptions,
      accountColors,
      connexionMode,
      email,
      id,
      language,
      name,
      firstname,
      lastname,
      phone,
      isWithImage,
      googlePhotoURL,
      turnstileToken
    }
  }
})

export const accountInsert_AsSupervisor = (account: Account, templateId: string | null) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/supervisor/account/insert",
    data: {
      account,
      templateId
    }
  }
})

export const accountPay = (token, email, price, count) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/payment",
    data: {
      token,
      email,
      price,
      count
    }
  }
})

export const accountQuit = (toSupervisor) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/quit",
    data: {
      toSupervisor
    }
  }
})

export const accountCleanDemo = () => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/demo/clean"
  }
})

export const accountUpdate = (account: any) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account",
    data: {
      account
    }
  }
})

export const accountUpdateColors = (colors: AccountColors) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/colors",
    data: {
      colors
    }
  }
})

export const accountUpdateOptions = (options: AccountOptions) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/options",
    data: {
      options
    }
  }
})

export const accountUpdateTemplateRules = (
  accountId: string,
  templateRule: AccountTemplateRule,
  allowedTemplates: string[]
) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/account/template/rules",
    data: {
      accountId,
      templateRule,
      allowedTemplates
    }
  }
})

export const accountUpdate_AsSupervisor = (account) => ({
  type: "API",
  payload: {
    method: "POST",
    url: "/supervisor/account/update",
    data: {
      account
    }
  }
})
